import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getMediaTypeFromUrl } from '../../../utils/helpers';

const ProductPreview = ({ 
  imageUrl, 
  title,
  caption,
  richDescription,
  productDetails = {},
  specifications = {}
}) => {
  const { isRTL } = useLanguage();
  const aspectRatio = specifications?.aspect_ratio || '1:1';
  
  // Get aspect ratio class for image
  const getAspectClass = () => {
    switch (aspectRatio) {
      case '1:1': return 'aspect-square';
      case '4:5': return 'aspect-[4/5]';
      case '16:9': return 'aspect-video';
      default: return 'aspect-square';
    }
  };

  const price = productDetails?.price;
  const currency = productDetails?.currency || 'SAR';
  const category = productDetails?.category;
  const brand = productDetails?.brand;
  const sku = productDetails?.sku;
  const inStock = productDetails?.in_stock !== false; // Default to true

  // Format price
  const formatPrice = () => {
    if (price === undefined || price === null) return null;
    return new Intl.NumberFormat(isRTL ? 'ar-SA' : 'en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(price);
  };

  return (
    <div className="w-full max-w-[400px] mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-lg">
        {/* Product Image/Video */}
        <div className={`relative bg-gray-100 ${getAspectClass()}`}>
          {imageUrl ? (
            getMediaTypeFromUrl(imageUrl) === 'video' ? (
              <video
                src={imageUrl}
                className="w-full h-full object-cover"
                controls
                muted
                playsInline
              />
            ) : (
              <img 
                src={imageUrl} 
                alt={title || 'Product'} 
                className="w-full h-full object-cover"
              />
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
              <div className="text-center p-4">
                <span className="text-5xl block mb-2">🛍️</span>
                <p className="text-gray-500 text-sm">
                  {isRTL ? 'صورة المنتج' : 'Product Image'}
                </p>
              </div>
            </div>
          )}
          
          {/* Stock Badge */}
          {!inStock && (
            <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold">
              {isRTL ? 'غير متوفر' : 'Out of Stock'}
            </div>
          )}
          
          {/* Favorite Button */}
          <button className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-white transition-colors">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>

        {/* Product Info */}
        <div className="p-4">
          {/* Category & Brand */}
          {(category || brand) && (
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {category && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {category}
                </span>
              )}
              {brand && (
                <span className="text-xs text-gray-600 font-medium">
                  {brand}
                </span>
              )}
            </div>
          )}

          {/* Title */}
          {title && (
            <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
              {title}
            </h3>
          )}

          {/* Description/Caption */}
          {richDescription ? (
            <div 
              className="text-sm text-gray-600 mb-3 leading-relaxed [&>p]:mb-2 [&>p:last-child]:mb-0 [&>h1]:text-base [&>h1]:font-bold [&>h1]:mb-1 [&>h2]:text-sm [&>h2]:font-bold [&>h2]:mb-1 [&>h3]:text-sm [&>h3]:font-semibold [&>h3]:mb-1 [&>ul]:list-disc [&>ul]:ml-4 [&>ol]:list-decimal [&>ol]:ml-4 [&>li]:mb-0.5 [&>a]:text-primary-600 [&>a]:hover:text-primary-700 [&>a]:underline [&>strong]:font-bold [&>em]:italic"
              dangerouslySetInnerHTML={{ __html: richDescription }}
            />
          ) : caption ? (
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
              {caption}
            </p>
          ) : null}

          {/* Price */}
          {price !== undefined && price !== null && (
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="text-xl font-bold text-gray-900">
                  {formatPrice()}
                </span>
              </div>
              {sku && (
                <span className="text-xs text-gray-400">
                  SKU: {sku}
                </span>
              )}
            </div>
          )}

          {/* Add to Cart Button */}
          <button 
            className={`w-full py-2 rounded-lg font-semibold transition-colors ${
              inStock
                ? 'bg-slate-900 text-white hover:bg-slate-800'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            disabled={!inStock}
          >
            {inStock 
              ? (isRTL ? 'أضف للسلة' : 'Add to Cart')
              : (isRTL ? 'غير متوفر' : 'Out of Stock')
            }
          </button>

          {/* Quick View Link */}
          <button className="w-full mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium">
            {isRTL ? 'عرض التفاصيل' : 'View Details'}
          </button>
        </div>
      </div>
      <p className="text-center text-xs text-gray-500 mt-2">
        {isRTL ? 'معاينة المنتج' : 'Product Preview'}
      </p>
    </div>
  );
};

export default ProductPreview;

